// src/admin/ManageMenu.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { Button, Spinner, Alert, Card, Form, Accordion } from 'react-bootstrap';
import { Bars3Icon, TrashIcon, LinkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const ManageMenu = () => {
    const [menuItems, setMenuItems] = useState([]);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Özel Link Formu
    const [customLink, setCustomLink] = useState({ title: '', url: '', icon: 'bi-link' });

    // Drag & Drop Referansları
    const dragItem = useRef();
    const dragOverItem = useRef();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Dinamik Sayfaları Getir
            const pagesSnapshot = await getDocs(collection(db, 'customPages'));
            const pagesList = pagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPages(pagesList);

            // Menü Yapısını Getir
            const menuRef = doc(db, 'settings', 'navbar');
            const menuSnap = await getDoc(menuRef);
            
            if (menuSnap.exists() && menuSnap.data().items) {
                setMenuItems(menuSnap.data().items);
            } else {
                // Varsayılan Menü (İlk Kurulum İçin)
                setMenuItems([
                    { id: '1', title: 'Homepage', textKey: 'Homepage', to: '/', icon: 'bi-house', parentId: null },
                    { id: '2', title: 'Community', textKey: 'Community', to: '/community', icon: 'bi-people', parentId: null },
                    { id: '3', title: 'Services', textKey: 'Services', to: '/self-publishing-service', icon: 'bi-pen', parentId: null },
                    { id: '4', title: 'Corporate', textKey: 'Corporate', to: '/corporate', icon: 'bi-briefcase', parentId: null },
                    { id: '5', title: 'Little Authors', textKey: 'LittleAuthors', to: '/little-authors', icon: 'bi-book', parentId: null },
                    { id: '6', title: 'Contact', textKey: 'Contact', to: '/contact', icon: 'bi-envelope', parentId: null }
                ]);
            }
        } catch (error) {
            setMessage({ text: 'Error fetching data: ' + error.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMenu = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'navbar'), { items: menuItems });
            setMessage({ text: 'Menu saved successfully!', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (error) {
            setMessage({ text: 'Failed to save menu.', type: 'danger' });
        }
        setSaving(false);
    };

    const addCustomLink = (e) => {
        e.preventDefault();
        if (!customLink.title || !customLink.url) return;
        
        const newItem = {
            id: Date.now().toString(),
            title: customLink.title,
            to: customLink.url,
            icon: customLink.icon,
            parentId: null
        };
        setMenuItems([...menuItems, newItem]);
        setCustomLink({ title: '', url: '', icon: 'bi-link' });
    };

    const addPageToMenu = (page) => {
        const newItem = {
            id: Date.now().toString(),
            title: page.title,
            to: `/${page.slug}`,
            icon: 'bi-file-earmark-text',
            parentId: null
        };
        setMenuItems([...menuItems, newItem]);
    };

    const removeMenuItem = (id) => {
        // Alt menüleri de sil
        const filtered = menuItems.filter(item => item.id !== id && item.parentId !== id);
        setMenuItems(filtered);
    };

    const updateParent = (id, newParentId) => {
        setMenuItems(menuItems.map(item => item.id === id ? { ...item, parentId: newParentId } : item));
    };

    // Sürükle Bırak Mantığı
    const handleDragStart = (e, index) => {
        dragItem.current = index;
    };

    const handleDragEnter = (e, index) => {
        dragOverItem.current = index;
    };

    const handleDragEnd = () => {
        const items = [...menuItems];
        const draggedItemContent = items.splice(dragItem.current, 1)[0];
        items.splice(dragOverItem.current, 0, draggedItemContent);
        dragItem.current = null;
        dragOverItem.current = null;
        setMenuItems(items);
    };

    if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

    // Ağaç yapısını düz listeye dönüştürerek hiyerarşiyi görselleştir
    const parentItems = menuItems.filter(item => !item.parentId);
    
    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold">Manage Navigation Menu</h2>
                    <p className="text-muted">Drag and drop items to reorder. Assign parents to create dropdown menus.</p>
                </div>
                <Button variant="primary" onClick={handleSaveMenu} disabled={saving} className="px-4 fw-bold shadow-sm">
                    {saving ? 'Saving...' : 'Save Menu'}
                </Button>
            </div>

            {message.text && <Alert variant={message.type}>{message.text}</Alert>}

            <div className="row g-4">
                {/* SOL PANEL: Öğe Ekleme (Pages & Custom Links) */}
                <div className="col-lg-4">
                    <Accordion defaultActiveKey="0" className="shadow-sm">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header><DocumentTextIcon style={{ width: '20px' }} className="me-2"/> Add Landing Pages</Accordion.Header>
                            <Accordion.Body className="p-0">
                                <ul className="list-group list-group-flush">
                                    {pages.length === 0 ? <li className="list-group-item text-muted">No pages found.</li> : 
                                     pages.map(page => (
                                        <li key={page.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <span className="small">{page.title}</span>
                                            <Button variant="outline-success" size="sm" onClick={() => addPageToMenu(page)}>Add</Button>
                                        </li>
                                    ))}
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header><LinkIcon style={{ width: '20px' }} className="me-2"/> Add Custom Link</Accordion.Header>
                            <Accordion.Body>
                                <Form onSubmit={addCustomLink}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Link Title</Form.Label>
                                        <Form.Control size="sm" type="text" value={customLink.title} onChange={(e) => setCustomLink({...customLink, title: e.target.value})} required />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">URL</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="https://..." value={customLink.url} onChange={(e) => setCustomLink({...customLink, url: e.target.value})} required />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small">Bootstrap Icon (Optional)</Form.Label>
                                        <Form.Control size="sm" type="text" placeholder="bi-link" value={customLink.icon} onChange={(e) => setCustomLink({...customLink, icon: e.target.value})} />
                                    </Form.Group>
                                    <Button type="submit" variant="dark" size="sm" className="w-100">Add to Menu</Button>
                                </Form>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>

                {/* SAĞ PANEL: Sürükle Bırak Menü Yapısı */}
                <div className="col-lg-8">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-white py-3">
                            <h6 className="m-0 fw-bold">Menu Structure</h6>
                        </Card.Header>
                        <Card.Body className="bg-light p-4">
                            {menuItems.length === 0 ? (
                                <p className="text-muted text-center my-4">Your menu is empty.</p>
                            ) : (
                                <div className="menu-list">
                                    {menuItems.map((item, index) => (
                                        <div 
                                            key={item.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnter={(e) => handleDragEnter(e, index)}
                                            onDragEnd={handleDragEnd}
                                            className="bg-white border rounded p-3 mb-2 shadow-sm d-flex justify-content-between align-items-center"
                                            style={{ cursor: 'grab', marginLeft: item.parentId ? '40px' : '0' }}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <Bars3Icon style={{ width: '20px' }} className="text-muted" />
                                                <div>
                                                    <div className="fw-bold">{item.textKey ? `[T] ${item.title}` : item.title}</div>
                                                    <small className="text-muted">{item.to}</small>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Select 
                                                    size="sm" 
                                                    value={item.parentId || ''} 
                                                    onChange={(e) => updateParent(item.id, e.target.value || null)}
                                                    style={{ width: '150px' }}
                                                >
                                                    <option value="">No Parent (Top Level)</option>
                                                    {parentItems.map(parent => parent.id !== item.id && (
                                                        <option key={parent.id} value={parent.id}>Child of: {parent.title}</option>
                                                    ))}
                                                </Form.Select>
                                                <Button variant="outline-danger" size="sm" onClick={() => removeMenuItem(item.id)}>
                                                    <TrashIcon style={{ width: '16px' }} />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ManageMenu;